import { z } from "zod";
import { IdPageQuery, IsoDateTime, pageOf, Uuid } from "./common";

/** Outbound webhook event types the host contract promises. Packages emit these with `emitWebhook`. */
export const WEBHOOK_EVENT_TYPES = [
  "lead.created",
  "lead.stage_changed",
  "post.published",
  "approval.requested",
  "campaign.paused",
  "budget.cap_hit",
  "conversion.recorded",
  "contact.unsubscribed",
  "action.requested",
  "journey.step",
] as const;
export type WebhookEventType = (typeof WEBHOOK_EVENT_TYPES)[number];

export const WEBHOOK_SIGNATURE_HEADER = "X-Imagora-Signature";
export const WEBHOOK_EVENT_ID_HEADER = "X-Imagora-Event-Id";
export const WEBHOOK_EVENT_TYPE_HEADER = "X-Imagora-Event";
/** Present on a delivery someone asked to send again, carrying the same event `id`, so a receiver may let it past its dedupe. */
export const WEBHOOK_REPLAY_HEADER = "X-Imagora-Replay";

/** The signed JSON body of every delivery. Receivers dedupe on `id`: delivery is at least once. */
export const WebhookEnvelope = z.object({
  id: Uuid,
  type: z.string(),
  tenantId: Uuid,
  tenantRef: z.string(),
  createdAt: IsoDateTime,
  data: z.unknown(),
});
export type WebhookEnvelope = z.infer<typeof WebhookEnvelope>;

export const WebhookEventType = z.enum(WEBHOOK_EVENT_TYPES);

export const WebhookEndpointDto = z.object({
  id: Uuid,
  /** `tenant` receives one tenant's events, `host` those of every tenant of the host. */
  level: z.enum(["tenant", "host"]),
  url: z.string(),
  /** Empty means all. */
  events: z.array(z.string()),
  enabled: z.boolean(),
  /** While set and in the future, deliveries carry a signature with the old secret too. */
  previousSecretExpiresAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type WebhookEndpointDto = z.infer<typeof WebhookEndpointDto>;
export const WebhookEndpointList = z.object({ items: z.array(WebhookEndpointDto) });

export const WebhookEndpointCreate = z.strictObject({
  url: z.string().trim().min(1).max(2_000),
  events: z.array(WebhookEventType).max(WEBHOOK_EVENT_TYPES.length).default([]),
});
export type WebhookEndpointCreate = z.infer<typeof WebhookEndpointCreate>;

/** The secret appears here once and never again. */
export const WebhookEndpointCreated = WebhookEndpointDto.extend({ secret: z.string() });
export type WebhookEndpointCreated = z.infer<typeof WebhookEndpointCreated>;

export const WebhookEndpointPatch = z
  .strictObject({
    url: z.string().trim().min(1).max(2_000),
    events: z.array(WebhookEventType).max(WEBHOOK_EVENT_TYPES.length),
    enabled: z.boolean(),
  })
  .partial();
export type WebhookEndpointPatch = z.infer<typeof WebhookEndpointPatch>;

export const WEBHOOK_ROTATION_DEFAULT_HOURS = 24;
export const WEBHOOK_ROTATION_MAX_HOURS = 7 * 24;

export const WebhookSecretRotate = z.strictObject({
  /** How long the old secret keeps signing, so a receiver can switch without losing a delivery. 0 retires it at once. */
  graceHours: z.number().int().min(0).max(WEBHOOK_ROTATION_MAX_HOURS).default(WEBHOOK_ROTATION_DEFAULT_HOURS),
});
export type WebhookSecretRotate = z.infer<typeof WebhookSecretRotate>;

export const WebhookSecretRotated = z.object({
  /** Appears here once and never again. */
  secret: z.string(),
  previousSecretExpiresAt: IsoDateTime.nullable(),
});
export type WebhookSecretRotated = z.infer<typeof WebhookSecretRotated>;

export const WEBHOOK_DELIVERY_STATUSES = ["pending", "delivering", "delivered", "failed", "skipped"] as const;

export const WebhookAttemptDto = z.object({
  attempt: z.number().int(),
  ok: z.boolean(),
  httpStatus: z.number().int().nullable(),
  error: z.string().nullable(),
  durationMs: z.number().int(),
  createdAt: IsoDateTime,
});

export const WebhookDeliveryDto = z.object({
  id: Uuid,
  eventId: Uuid,
  type: z.string(),
  /** Null: the host's own webhook URL. */
  endpointId: Uuid.nullable(),
  status: z.enum(WEBHOOK_DELIVERY_STATUSES),
  attempts: z.number().int(),
  nextAttemptAt: IsoDateTime,
  deliveredAt: IsoDateTime.nullable(),
  lastError: z.string().nullable(),
  replayOfId: Uuid.nullable(),
  createdAt: IsoDateTime,
});
export type WebhookDeliveryDto = z.infer<typeof WebhookDeliveryDto>;

export const WebhookDeliveryDetail = WebhookDeliveryDto.extend({
  envelope: WebhookEnvelope,
  deliveryAttempts: z.array(WebhookAttemptDto),
});
export type WebhookDeliveryDetail = z.infer<typeof WebhookDeliveryDetail>;

export const WebhookDeliveryPage = pageOf(WebhookDeliveryDto);

export const WebhookDeliveryListQuery = IdPageQuery.extend({
  endpointId: Uuid.optional(),
  eventId: Uuid.optional(),
  status: z.enum(WEBHOOK_DELIVERY_STATUSES).optional(),
  type: z.string().max(100).optional(),
});
export type WebhookDeliveryListQuery = z.infer<typeof WebhookDeliveryListQuery>;
